import moment from "moment-timezone"
import { FEATURE_NEW_PLACEMENT_FLOW } from "@hooks/useFeature"

// Staff-search tab scopes, shared by the search pages and deep links into them
export const ALL_SCOPE = "all"
export const RECOMMENDED_SCOPE = "recommended"
export const SHORTLIST_SCOPE = "shortlist"
export const STAFF_SEARCH_SCOPES = [ALL_SCOPE, RECOMMENDED_SCOPE, SHORTLIST_SCOPE]

// Whether the current user is carrying the new placement flow flag. The flag is a
// login-time session snapshot (it survives masquerade via the feature carry-through),
// so `user.features` is the authoritative source on the frontend.
export const hasNewPlacementFlow = (user) => {
  const features = user?.features
  return Array.isArray(features) && features.includes(FEATURE_NEW_PLACEMENT_FLOW)
}

// The staff-search route for this user: the new /staff-search-v2 page when the new
// placement flow is on, otherwise the legacy /staff-search. `query` is an optional
// pre-built query string (without the leading "?").
export const staffSearchPath = (user, query = "") => {
  const base = hasNewPlacementFlow(user) ? "/staff-search-v2" : "/staff-search"
  return query ? `${base}?${query}` : base
}

// The interview-requests tab for this user: the Applied stage of the new My Staffing
// pipeline when the new placement flow is on, otherwise the legacy placements tab.
export const interviewRequestsPath = (user) =>
  hasNewPlacementFlow(user) ? "/my-staffing/applied" : "/placements/interview_requests"

// This user's staffing page: the new pipeline when the new placement flow is on,
// otherwise the legacy On Review list.
export const myStaffingPath = (user) => (hasNewPlacementFlow(user) ? "/my-staffing" : "/placements/on_review")

// Whether a profile opened from staff search should offer the placement-free interview invite.
//
// The tile always offers it, but the profile header is built from getButtonValues, whose branches
// are all status-driven - and a participant found through search has no placement with this
// employer, so the drawer came back with nothing to do (PCS-259). Recommendations were unaffected:
// they take their own branch. The two exceptions mirror the tile's own rules - a recommendation
// already has its invite, and an id_eq lookup offers "Make an Offer" instead.
export const offersPlacementFreeInvite = ({ recommendationId, idEq, direct } = {}) => {
  if (recommendationId) return false
  if (idEq && !direct) return false

  return true
}

// Staff-search URL params for a staffing need: its criteria as filters, plus its id so
// match scoring uses that need only.
export const staffingNeedSearchParams = (staffingNeed) => {
  const q = { staffing_need_id: staffingNeed.id }
  // _in rather than _eq so the Skill filter shows it ticked and extra skills widen the search
  if (staffingNeed.primary_skill_id) q.skills_skill_id_in = staffingNeed.primary_skill_id
  if (staffingNeed.availability_start) q.start_date = moment(staffingNeed.is_available_from_2).format("YYYY-MM-DD")
  if (staffingNeed.availability_end) q.end_date = moment(staffingNeed.is_available_till).format("YYYY-MM-DD")
  if (staffingNeed.skills_skill_groups_id_eq) q.skills_skill_groups_id_eq = staffingNeed.skills_skill_groups_id_eq
  if (staffingNeed.programme_group_id) q.programme_groups_id_eq = staffingNeed.programme_group_id
  if (staffingNeed.participant_age_gteq) q.participant_age_gteq = staffingNeed.participant_age_gteq
  if (staffingNeed.qualifications_related_to_children_eq)
    q.qualifications_related_to_children_eq = staffingNeed.qualifications_related_to_children_eq
  if (staffingNeed.general_counselor_willing_eq)
    q.general_counselor_willing_eq = staffingNeed.general_counselor_willing_eq
  if (staffingNeed.special_needs_willing) q.special_needs_willing = "Yes - Full Care"

  // We only want to pass one or the other, not both
  if (staffingNeed.student_status_student && !staffingNeed.student_status_non_student) {
    q.master_current_student = "yes"
  } else if (!staffingNeed.student_status_student && staffingNeed.student_status_non_student) {
    q.master_current_student = "no"
  }

  const params = new URLSearchParams()
  Object.entries(q).forEach(([key, value]) => params.append(key, value))
  ;(staffingNeed.user_gender_in || []).forEach((gender) => params.append("user_gender_in", gender))
  return params
}
